import { IRoadmap } from "@/utils/types";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { outlineButtonTw } from "./button";
import { useEffect, useRef, useState } from "react";
import { IRoadmapArt, parseRoadmap } from "./roadmapParse";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const popTransition = {
  type: "spring" as const,
  stiffness: 260,
  damping: 24,
};

/** The artwork comes from the CMS; cache the fetch+parse per URL. */
const artCache = new Map<string, Promise<IRoadmapArt | null>>();

function useRoadmapArt(svgRef?: string) {
  const [art, setArt] = useState<IRoadmapArt | null>(null);
  // Proxied through our own API: the Sanity file CDN blocks cross-origin fetches.
  const url = svgRef ? `/api/roadmap-svg?ref=${encodeURIComponent(svgRef)}` : null;

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let promise = artCache.get(url);
    if (!promise) {
      promise = fetch(url)
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then(parseRoadmap)
        .catch(() => {
          artCache.delete(url);
          return null;
        });
      artCache.set(url, promise);
    }
    promise.then((parsed) => {
      if (!cancelled) setArt(parsed);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return art;
}

/** The full artwork with every step visible (no scroll choreography). */
function RoadmapSvg({
  art,
  className,
  label,
}: {
  art: IRoadmapArt;
  className?: string;
  label?: string;
}) {
  return (
    <svg
      viewBox={art.viewBox}
      fill="none"
      className={className}
      role="img"
      aria-label={label ?? "Roadmap"}
    >
      <g dangerouslySetInnerHTML={{ __html: art.defs }} />
      <g dangerouslySetInnerHTML={{ __html: art.base }} />
      {art.steps.map((step, i) => (
        <g key={i} dangerouslySetInnerHTML={{ __html: step }} />
      ))}
    </svg>
  );
}

/** Full-screen modal where the roadmap can be zoomed (pinch or buttons) and panned (scroll). */
function RoadmapModal({
  art,
  label,
  onClose,
}: {
  art: IRoadmapArt;
  label?: string;
  onClose: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(2);
  const [dragging, setDragging] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Start centered on the artwork.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
  }, []);

  /** Change zoom while keeping the given container point (default: center) fixed. */
  const zoomTo = (next: number, cx?: number, cy?: number) => {
    const el = scrollRef.current;
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    if (!el || clamped === zoomRef.current) {
      setZoom(clamped);
      return;
    }
    const px = cx ?? el.clientWidth / 2;
    const py = cy ?? el.clientHeight / 2;
    const factor = clamped / zoomRef.current;
    const scrollLeft = (el.scrollLeft + px) * factor - px;
    const scrollTop = (el.scrollTop + py) * factor - py;
    setZoom(clamped);
    requestAnimationFrame(() => {
      el.scrollLeft = scrollLeft;
      el.scrollTop = scrollTop;
    });
  };
  const zoomToRef = useRef(zoomTo);
  zoomToRef.current = zoomTo;

  // Click-and-drag panning for mouse users.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let last: { x: number; y: number } | null = null;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      last = { x: e.clientX, y: e.clientY };
      setDragging(true);
      e.preventDefault(); // no text/image selection while dragging
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!last) return;
      el.scrollLeft -= e.clientX - last.x;
      el.scrollTop -= e.clientY - last.y;
      last = { x: e.clientX, y: e.clientY };
    };
    const onMouseUp = () => {
      last = null;
      setDragging(false);
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Pinch to zoom; single-finger panning is native scrolling.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let pinch: { dist: number; zoom: number } | null = null;
    const dist = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinch = { dist: dist(e.touches), zoom: zoomRef.current };
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinch) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        zoomToRef.current(pinch.zoom * (dist(e.touches) / pinch.dist), cx, cy);
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  // bg-white stays here: these float over the pannable artwork.
  const controlTw = `${outlineButtonTw} bg-white`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.6 }}
      className="fixed inset-0 z-[100] bg-white"
    >
      <div
        ref={scrollRef}
        className={`absolute inset-0 overflow-auto overscroll-contain ${
          dragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{ touchAction: "pan-x pan-y" }}
      >
        <div className="min-h-full flex items-center">
          <div className="shrink-0 py-20" style={{ width: `${zoom * 100}%` }}>
            <RoadmapSvg art={art} className="w-full h-auto" label={label} />
          </div>
        </div>
      </div>

      <button
        onClick={onClose}
        aria-label="Sluiten"
        className="absolute top-4 right-4 w-10 h-10 font-display text-xl text-darkGrey hover:scale-110 transition-all active:scale-100"
      >
        ✕
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
        <button
          onClick={() => zoomTo(zoomRef.current / 1.5)}
          aria-label="Uitzoomen"
          className={controlTw}
        >
          −
        </button>
        <button
          onClick={() => zoomTo(zoomRef.current * 1.5)}
          aria-label="Inzoomen"
          className={controlTw}
        >
          +
        </button>
      </div>
    </motion.div>
  );
}

/** Mobile: white panel with title/description/button and a static preview image. */
function RoadmapMobile({ art, ...props }: IRoadmap & { art: IRoadmapArt }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="component-padding">
      <div className="flex flex-col items-center component-gap bg-white p-8 text-darkGrey text-center">
        {props.title && <p className="heading">{props.title}</p>}
        {props.description && <p className="opacity-80">{props.description}</p>}
        <button onClick={() => setOpen(true)} className={outlineButtonTw}>
          {props.buttonLabel || "Bekijk de roadmap"}
        </button>
        <button
          onClick={() => setOpen(true)}
          aria-label={props.buttonLabel || "Bekijk de roadmap"}
          className="w-full"
        >
          <RoadmapSvg art={art} className="w-full h-auto" label={props.title} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <RoadmapModal
            art={art}
            label={props.title}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/** Desktop: the section hugs the artwork (no scroll track) — the steps pop in
 *  staggered once it's in view, and a button opens the zoomable modal viewer. */
function RoadmapDesktop({ art, ...props }: IRoadmap & { art: IRoadmapArt }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const inView = useInView(svgRef, { once: true, amount: 0.5 });
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div className="flex flex-col items-center component-gap component-padding text-center pb-8">
        {props.title && <p className="heading">{props.title}</p>}
        {props.description && (
          <p className="opacity-80 max-w-3xl">{props.description}</p>
        )}
        <button onClick={() => setOpen(true)} className={outlineButtonTw}>
          {props.buttonLabel || "Bekijk de roadmap"}
        </button>
      </div>

      <svg
        ref={svgRef}
        viewBox={art.viewBox}
        fill="none"
        className="w-full h-auto"
        role="img"
        aria-label={props.title ?? "Roadmap"}
      >
        <g dangerouslySetInnerHTML={{ __html: art.defs }} />
        <g dangerouslySetInnerHTML={{ __html: art.base }} />
        {art.steps.map((step, i) => (
          <motion.g
            key={i}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.7 }}
            transition={{ ...popTransition, delay: i * 0.35 }}
            style={{
              transformBox: "fill-box",
              originX: 0.5,
              originY: 0.5,
            }}
            dangerouslySetInnerHTML={{ __html: step }}
          />
        ))}
      </svg>

      <AnimatePresence>
        {open && (
          <RoadmapModal
            art={art}
            label={props.title}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export function Roadmap(props: IRoadmap) {
  const [isMobile, setIsMobile] = useState(false);
  const art = useRoadmapArt(props.svg?.asset?._ref);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // No SVG uploaded (or still loading): render the copy without the artwork.
  if (!art) {
    return props.title || props.description ? (
      <div className="flex flex-col items-center component-gap component-padding text-center">
        {props.title && <p className="heading">{props.title}</p>}
        {props.description && (
          <p className="opacity-80 max-w-3xl">{props.description}</p>
        )}
      </div>
    ) : null;
  }

  return isMobile ? (
    <RoadmapMobile art={art} {...props} />
  ) : (
    <RoadmapDesktop art={art} {...props} />
  );
}
